import React, {FC, PropsWithChildren, ReactNode, useMemo} from 'react';
import styled from 'styled-components';

import {Avatar} from '../../../../../libs/shared/ui/src/components/avatar/avatar';
import {AvatarImage} from '../../../../../libs/shared/ui/src/components/avatar/avatarImage';
import {BoxedIcon} from '../../../../../libs/shared/ui/src/components/icon/boxedIcon';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {ChildrenCell} from '../../../../../libs/shared/ui/src/components/tables/cells/childrenCell';
import {TableCellProps} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCellTypes';
import {StyledTextCellValueDiv} from '../../../../../libs/shared/ui/src/components/tables/cells/textCell';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {EmojiDefinitionAndSkinTone} from '../../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {StyledBoxedEmoji} from '../../../../settings/tags/settingsTagNameCell';
import {SheetEmoji} from '../../emoji/sheetEmoji';

/*
 * Props.
 */

interface NameCellProps extends TableCellProps<string> {
  iconName?: IconName;
  imageUrl?: string;
  emojiDefinition?: EmojiDefinitionAndSkinTone;
  description?: ReactNode;
}

/*
 * Styles.
 */

const StringNameCellValueDiv = styled(StyledTextCellValueDiv)`
  ${flexCenter};
`;

const StyledIconWrapperDiv = styled.div`
  margin: 0 12px;
`;

const StyledBoxedEmojiDiv = styled(StyledBoxedEmoji)`
  margin: 0 12px;
`;

const StyledContentDiv = styled.div`
  display: grid;
  grid-template-rows: max-content max-content;
  grid-row-gap: 4px;

  &:first-child {
    padding-left: 12px;
  }
`;

const StyledValueDiv = styled.div`
  font-weight: ${fontWeights.semibold};
`;

const StyledDescriptionDiv = styled.div<{$isSelected: boolean}>`
  font-size: ${fontSizes.verySmall};
  color: ${(p) => (p.$isSelected ? p.theme.greys.shade30 : p.theme.greys.shade70)};
`;

const StyledAvatar = styled(Avatar)`
  margin: 0 12px;
`;

/*
 * Component.
 */

export const NameCell: FC<PropsWithChildren<NameCellProps>> = (props) => {
  const theme = useUserTheme();
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    value,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    iconName,
    imageUrl,
    emojiDefinition,
    description,
  } = props;

  const maybeRenderIcon = () => {
    if (emojiDefinition) {
      return (
        <StyledBoxedEmojiDiv>
          <SheetEmoji emoji={emojiDefinition.emoji} skinTone={emojiDefinition.skinTone} size={20} />
        </StyledBoxedEmojiDiv>
      );
    }

    if (imageUrl) {
      return (
        <StyledAvatar size={40}>
          <AvatarImage imageUrl={imageUrl} />
        </StyledAvatar>
      );
    }

    if (iconName) {
      return (
        <StyledIconWrapperDiv>
          <BoxedIcon
            name={iconName}
            color={theme.greys.shade70}
            borderColor={theme.greys.shade30}
            borderStyle="solid"
            size={size}
          />
        </StyledIconWrapperDiv>
      );
    }

    return null;
  };

  const cellContent = useMemo(() => {
    if (description === undefined) {
      return (
        <StyledContentDiv>
          <StyledValueDiv>{value}</StyledValueDiv>
        </StyledContentDiv>
      );
    }

    return (
      <StyledContentDiv>
        <StyledValueDiv>{value}</StyledValueDiv>
        <StyledDescriptionDiv $isSelected={Boolean(isSelected)}>{description}</StyledDescriptionDiv>
      </StyledContentDiv>
    );
  }, [description, isSelected, value]);

  return (
    <ChildrenCell
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
    >
      <StringNameCellValueDiv $isSelected={Boolean(isSelected)}>
        {maybeRenderIcon()}
        {cellContent}
      </StringNameCellValueDiv>
    </ChildrenCell>
  );
};
